import './despachar-envio-modal.js';
import type { Envio } from '@pgcustom/shared-types';
import type { Resultado } from '../../core/resultado.js';
import type { PgTable, PgTableColumn, PgTableRow, PgChart, PgKpiCard } from '@pgcustom/design-system';
import type { EnviosApi } from './envios.api.js';
import type { PermissoesEnvios } from './envios.permissoes.js';
import { formatarDataHora } from '../pedidos/formatacao.js';
import type { ErpDespacharEnvioModal } from './despachar-envio-modal.js';

const COLUNAS: PgTableColumn[] = [
  { key: 'pedidoId', label: 'Pedido' },
  { key: 'status', label: 'Status' },
  { key: 'transportadora', label: 'Transportadora' },
  { key: 'codigoRastreio', label: 'Rastreio' },
  { key: 'criadoEm', label: 'Criado em' },
];

const ROTULO_STATUS: Record<Envio['status'], string> = {
  criado: 'Aguardando despacho',
  despachado: 'Em trânsito',
  entregue: 'Entregue',
};

const TOM_STATUS: Record<Envio['status'], 'neutro' | 'destaque' | 'sucesso'> = {
  criado: 'neutro',
  despachado: 'destaque',
  entregue: 'sucesso',
};

const COR_STATUS: Record<Envio['status'], string> = {
  criado: 'var(--pg-color-texto-secundario)',
  despachado: 'var(--pg-color-destaque)',
  entregue: 'var(--pg-color-sucesso)',
};

/**
 * `<erp-envios-page>` — Envios (Sprint 9, `ROADMAP.md`: "OPS concluída
 * gera envio rastreável"). Sem criação manual — todo envio nasce do
 * listener de `ops.concluida`. Clique num envio `criado` abre o modal
 * de despacho; clique num envio `despachado` marca como entregue
 * diretamente (ação real, não destrutiva — mesmo padrão de "Atribuir a
 * mim" em Produção). Proxy de frete (Sprint 15) fora desta tela — sem
 * dado de endereço/dimensão de pacote capturado em nenhum módulo ainda.
 */
export class ErpEnviosPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _enviosApi: EnviosApi | null = null;
  private _permissoes: PermissoesEnvios = { ler: false, atualizar: false };

  private resultadoEnvios: Resultado<Envio[]> = { status: 'carregando' };
  private enviosAtuais: Envio[] = [];
  private mensagemAcao = '';

  private modalDespachar!: ErpDespacharEnvioModal;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set enviosApi(valor: EnviosApi) {
    this._enviosApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set permissoes(valor: PermissoesEnvios) {
    this._permissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._enviosApi) void this.carregar();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .topo { display: flex; align-items: center; justify-content: space-between; gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-5, 24px); }
        h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: var(--pg-font-size-xl, 1.5rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: 0; }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-top: 2px; }
        .kpis {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: var(--pg-space-4, 16px);
          margin-bottom: var(--pg-space-4, 16px);
        }
        .layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(260px, 320px); gap: var(--pg-space-4, 16px); align-items: start; }
        @media (max-width: 900px) { .layout { grid-template-columns: 1fr; } }
        .dica { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-desabilitado, #5c626b); margin-top: var(--pg-space-3, 12px); }
        .erro-acao { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-erro, #e5484d); margin-top: var(--pg-space-3, 12px); }
        .erro-acao:empty { display: none; }
      </style>
      <div class="topo">
        <div>
          <h1>Envios</h1>
          <div class="subtitulo">Rastreamento de envios gerados a partir de OPS concluídas</div>
        </div>
      </div>

      <div class="kpis">
        <pg-kpi-card id="kpi-aguardando" label="Aguardando despacho" icone="envios" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-transito" label="Em trânsito" icone="envios" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-entregue" label="Entregues" icone="envios" estado="carregando"></pg-kpi-card>
      </div>

      <div class="layout">
        <pg-card padding="compacto">
          <span slot="header">Envios</span>
          <div id="envios"></div>
        </pg-card>
        <pg-card padding="compacto">
          <span slot="header">Distribuição por status</span>
          <div id="donut-status"><pg-empty-state variant="carregando" titulo="Distribuição"></pg-empty-state></div>
        </pg-card>
      </div>
      ${this._permissoes.atualizar ? '<p class="dica">Clique em um envio aguardando despacho para informar a transportadora, ou em um envio em trânsito para marcar como entregue.</p>' : ''}
      <p class="erro-acao">${escaparHtml(this.mensagemAcao)}</p>

      <erp-despachar-envio-modal id="modal-despachar"></erp-despachar-envio-modal>
    `;

    this.modalDespachar = this.root.querySelector('#modal-despachar') as ErpDespacharEnvioModal;
    if (this._enviosApi) this.modalDespachar.enviosApi = this._enviosApi;
    this.modalDespachar.aoSalvar = () => void this.carregar();

    this.root.querySelector('#envios')?.addEventListener('pg-row-click', (evento) => {
      if (!this._permissoes.atualizar) return;
      const { index } = (evento as CustomEvent<{ row: PgTableRow; index: number }>).detail;
      const envio = this.enviosAtuais[index];
      if (!envio) return;

      if (envio.status === 'criado') {
        this.modalDespachar.abrir(envio);
      } else if (envio.status === 'despachado') {
        void this.marcarComoEntregue(envio.id);
      }
    });

    this.atualizar();
  }

  private async marcarComoEntregue(id: string): Promise<void> {
    if (!this._enviosApi) return;
    try {
      await this._enviosApi.marcarComoEntregue(id);
      this.mensagemAcao = '';
      void this.carregar();
    } catch {
      this.mensagemAcao = 'não foi possível marcar como entregue. tente novamente.';
      this.renderEsqueleto();
    }
  }

  private async carregar(): Promise<void> {
    const api = this._enviosApi;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultadoEnvios = { status: 'sem-permissao' };
      this.atualizar();
      return;
    }

    this.resultadoEnvios = { status: 'carregando' };
    this.atualizar();
    this.resultadoEnvios = await api.listar();
    this.atualizar();
  }

  private atualizar(): void {
    const container = this.root.querySelector('#envios');
    if (!container) return;

    if (this.resultadoEnvios.status === 'ok') {
      this.enviosAtuais = this.resultadoEnvios.dado;
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS;
      tabela.rows = this.enviosAtuais.map((e) => ({
        pedidoId: e.pedidoId.slice(0, 8) + '…',
        status: { texto: ROTULO_STATUS[e.status], tom: TOM_STATUS[e.status] },
        transportadora: e.transportadora ?? '—',
        codigoRastreio: e.codigoRastreio ?? '—',
        criadoEm: formatarDataHora(e.criadoEm),
      }));
      this.atualizarKpisEDonut(this.enviosAtuais);
      return;
    }

    this.enviosAtuais = [];
    const { variant, titulo, descricao } = estadoVazio(this.resultadoEnvios);
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
    this.aplicarEstadoKpisEDonut(this.resultadoEnvios);
  }

  private atualizarKpisEDonut(envios: Envio[]): void {
    const contagem: Record<Envio['status'], number> = { criado: 0, despachado: 0, entregue: 0 };
    for (const e of envios) contagem[e.status]++;

    this.definirKpi('kpi-aguardando', String(contagem.criado));
    this.definirKpi('kpi-transito', String(contagem.despachado));
    this.definirKpi('kpi-entregue', String(contagem.entregue));

    const donut = this.root.querySelector('#donut-status');
    if (!donut) return;
    donut.innerHTML = '<pg-chart></pg-chart>';
    const chart = donut.querySelector('pg-chart') as PgChart;
    chart.type = 'donut';
    chart.series = [{ key: 'valor', label: '' }];
    chart.points = (Object.keys(ROTULO_STATUS) as Envio['status'][]).map((status) => ({
      label: ROTULO_STATUS[status],
      values: { valor: contagem[status] },
      color: COR_STATUS[status],
    }));
    chart.formatarValor = (valor) => String(valor);
  }

  private aplicarEstadoKpisEDonut(resultado: Resultado<Envio[]>): void {
    const { variant, titulo, descricao } = estadoVazio(resultado);
    for (const id of ['kpi-aguardando', 'kpi-transito', 'kpi-entregue']) {
      const card = this.root.querySelector(`#${id}`) as PgKpiCard | null;
      if (!card) continue;
      if (resultado.status === 'vazio') {
        card.setAttribute('estado', 'ok');
        card.setAttribute('valor', '0');
      } else {
        card.setAttribute('estado', variant === 'carregando' ? 'carregando' : 'indisponivel');
        if (variant !== 'carregando') card.setAttribute('dica', descricao);
      }
    }
    const donut = this.root.querySelector('#donut-status');
    if (donut) donut.innerHTML = `<pg-empty-state variant="${variant}" titulo="Distribuição" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }

  private definirKpi(id: string, valor: string): void {
    const card = this.root.querySelector(`#${id}`) as PgKpiCard | null;
    if (!card) return;
    card.setAttribute('estado', 'ok');
    card.setAttribute('valor', valor);
  }
}

function estadoVazio(resultado: Resultado<Envio[]>): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo: 'Envios', descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo: 'Envios', descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo: 'Envios', descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: 'Nenhum envio', descricao: 'envios aparecem aqui automaticamente quando uma OPS é concluída' };
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-envios-page', ErpEnviosPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-envios-page': ErpEnviosPage;
  }
}
